package com.schoolconnect.app.feature.schools.details

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.ui.draw.clip
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AdminSummaryMetrics
import com.schoolconnect.app.designsystem.AppBackground
import com.schoolconnect.app.designsystem.AppBorder
import com.schoolconnect.app.designsystem.AppInfoBg
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppPrimarySoft
import com.schoolconnect.app.designsystem.AppSecondaryBackground
import com.schoolconnect.app.designsystem.AppSoftSurface
import com.schoolconnect.app.designsystem.AppSurface
import com.schoolconnect.app.designsystem.AppSuccess
import com.schoolconnect.app.designsystem.AppSuccessSoft
import com.schoolconnect.app.designsystem.list.AdminListStatCardShell
import com.schoolconnect.app.datasource.network.response.SchoolAnalyticsResponse
import com.schoolconnect.app.util.decodeImageBitmap
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.arrow_left
import ischoolconnect.sharedui.generated.resources.book
import ischoolconnect.sharedui.generated.resources.building_school
import ischoolconnect.sharedui.generated.resources.calendar
import ischoolconnect.sharedui.generated.resources.clock
import ischoolconnect.sharedui.generated.resources.info_circle
import ischoolconnect.sharedui.generated.resources.open_book
import ischoolconnect.sharedui.generated.resources.school_users
import ischoolconnect.sharedui.generated.resources.user
import ischoolconnect.sharedui.generated.resources.users
import org.jetbrains.compose.resources.painterResource

internal val SchoolDetailsBackground = AppSecondaryBackground
internal val SchoolDetailsCard = AppSurface
internal val SchoolDetailsPrimary = AppPrimary
internal val SchoolDetailsNavy = AppNavy
internal val SchoolDetailsMuted = AppMuted
internal val SchoolDetailsBorder = AppBorder
internal val SchoolDetailsLine = Color(0xFFE5E7EB)
internal val SchoolDetailsSuccess = AppSuccess
internal val SchoolDetailsSuccessBg = AppSuccessSoft
internal val SchoolDetailsIconTileBg = AppPrimarySoft

internal data class SchoolDetailRowUi(
    val label: String,
    val value: String,
    val valueColor: Color? = null
)

internal data class SchoolDetailStatUi(
    val label: String,
    val value: String,
    val accent: Color,
    val accentBg: Color,
    val icon: Painter
)

internal data class SchoolDetailAuditUi(
    val createdOn: String,
    val createdBy: String,
    val lastUpdated: String,
    val updatedBy: String
)

internal fun schoolBoardLabel(codeOrValue: String): String = when (codeOrValue) {
    "2050" -> "CBSE"
    "2051" -> "ICSE"
    "2052" -> "State Board"
    "2053" -> "IGCSE"
    "2054" -> "Other"
    else -> codeOrValue
}

@Composable
internal fun schoolDetailsStats(analytics: SchoolAnalyticsResponse?): List<SchoolDetailStatUi> {
    val metrics = analytics?.metrics
    val hasCurrentYear = analytics?.currentAcademicYear?.label?.isNotBlank() == true
    fun countText(value: Int): String = if (hasCurrentYear) value.toString() else "—"
    return listOf(
        SchoolDetailStatUi(
            label = "Staff",
            value = countText(metrics?.staff?.total ?: 0),
            accent = AdminSummaryMetrics.Staff.accent,
            accentBg = AdminSummaryMetrics.Staff.soft,
            icon = painterResource(Res.drawable.school_users)
        ),
        SchoolDetailStatUi(
            label = "Classes",
            value = countText(metrics?.classes?.total ?: 0),
            accent = AdminSummaryMetrics.Classes.accent,
            accentBg = AdminSummaryMetrics.Classes.soft,
            icon = painterResource(Res.drawable.book)
        ),
        SchoolDetailStatUi(
            label = "Subjects",
            value = countText(metrics?.subjects?.total ?: 0),
            accent = AdminSummaryMetrics.Subjects.accent,
            accentBg = AdminSummaryMetrics.Subjects.soft,
            icon = painterResource(Res.drawable.open_book)
        ),
        SchoolDetailStatUi(
            label = "Students",
            value = countText(metrics?.students?.total ?: 0),
            accent = AdminSummaryMetrics.Students.accent,
            accentBg = AdminSummaryMetrics.Students.soft,
            icon = painterResource(Res.drawable.user)
        ),
        SchoolDetailStatUi(
            label = "Parents",
            value = countText(metrics?.parents?.total ?: 0),
            accent = AdminSummaryMetrics.Parents.accent,
            accentBg = AdminSummaryMetrics.Parents.soft,
            icon = painterResource(Res.drawable.users)
        )
    )
}

@Composable
internal fun SchoolDetailsStatsShimmer(besideIdentity: Boolean = true) {
    val transition = rememberInfiniteTransition(label = "school_details_stats_shimmer")
    val shimmerX by transition.animateFloat(
        initialValue = -300f,
        targetValue = 900f,
        animationSpec = infiniteRepeatable(tween(1100), RepeatMode.Restart),
        label = "school_details_stats_shimmer_x"
    )
    val shimmerBrush = Brush.linearGradient(
        colors = listOf(Color(0xFFE9EEF7), Color(0xFFF8FAFD), Color(0xFFE9EEF7)),
        start = Offset(shimmerX, 0f),
        end = Offset(shimmerX + 220f, 0f)
    )
    if (besideIdentity) {
        Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
            repeat(5) {
                Box(
                    modifier = Modifier
                        .width(148.dp)
                        .height(64.dp)
                        .clip(RoundedCornerShape(10.dp))
                        .background(shimmerBrush)
                )
            }
        }
    } else {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            repeat(2) { row ->
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                    repeat(if (row == 0) 3 else 2) {
                        Box(
                            modifier = Modifier
                                .weight(1f)
                                .height(64.dp)
                                .clip(RoundedCornerShape(10.dp))
                                .background(shimmerBrush)
                        )
                    }
                }
            }
        }
    }
}

internal fun schoolDetailsAudit(state: SchoolDetailsUiState): SchoolDetailAuditUi = SchoolDetailAuditUi(
    createdOn = state.createdOn,
    createdBy = state.createdBy,
    lastUpdated = state.lastUpdated,
    updatedBy = state.updatedBy
)

@Composable
internal fun SchoolDetailsHeader(
    state: SchoolDetailsUiState,
    onBack: () -> Unit
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Surface(
            modifier = Modifier.size(48.dp),
            shape = RoundedCornerShape(8.dp),
            color = SchoolDetailsCard,
            border = BorderStroke(1.dp, SchoolDetailsBorder),
            onClick = onBack
        ) {
            Box(contentAlignment = Alignment.Center) {
                Icon(
                    painter = painterResource(Res.drawable.arrow_left),
                    contentDescription = "Back",
                    tint = SchoolDetailsNavy,
                    modifier = Modifier.size(22.dp)
                )
            }
        }
        Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(
                text = "School Details",
                style = MaterialTheme.typography.headlineSmall,
                fontSize = 26.sp,
                fontWeight = FontWeight.ExtraBold,
                color = SchoolDetailsNavy
            )
            Text(
                text = "View school profile and key information",
                style = MaterialTheme.typography.bodyMedium,
                fontSize = 13.sp,
                fontWeight = FontWeight.Normal,
                color = SchoolDetailsMuted
            )
        }
    }
}

@Composable
internal fun SchoolDetailsSummaryCard(
    state: SchoolDetailsUiState,
    statsBesideIdentity: Boolean
) {
    val schoolLogo: ImageBitmap? = remember(state.schoolLogoBytes) {
        state.schoolLogoBytes?.let(::decodeImageBitmap)
    }
    val stats = schoolDetailsStats(state.analytics)
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(10.dp),
        colors = CardDefaults.cardColors(containerColor = SchoolDetailsCard),
        border = BorderStroke(1.dp, SchoolDetailsBorder)
    ) {
        if (statsBesideIdentity) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 22.dp, vertical = 18.dp),
                horizontalArrangement = Arrangement.spacedBy(16.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SchoolDetailsIdentity(state = state, schoolLogo = schoolLogo, modifier = Modifier.weight(1f))
                SchoolDetailsSummaryStats(
                    stats = stats,
                    isLoading = state.isAnalyticsLoading,
                    besideIdentity = true
                )
            }
        } else {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 22.dp, vertical = 18.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                SchoolDetailsIdentity(state = state, schoolLogo = schoolLogo)
                SchoolDetailsSummaryStats(
                    stats = stats,
                    isLoading = state.isAnalyticsLoading,
                    besideIdentity = false
                )
            }
        }
    }
}

@Composable
private fun SchoolDetailsIdentity(
    state: SchoolDetailsUiState,
    schoolLogo: ImageBitmap?,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier,
        horizontalArrangement = Arrangement.spacedBy(18.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .size(72.dp)
                .background(SchoolDetailsIconTileBg, CircleShape),
            contentAlignment = Alignment.Center
        ) {
            if (schoolLogo != null) {
                Image(
                    bitmap = schoolLogo,
                    contentDescription = "School logo",
                    contentScale = androidx.compose.ui.layout.ContentScale.Crop,
                    modifier = Modifier
                        .size(64.dp)
                        .clip(CircleShape)
                )
            } else {
                Icon(
                    painter = painterResource(Res.drawable.building_school),
                    contentDescription = null,
                    tint = SchoolDetailsPrimary,
                    modifier = Modifier.size(38.dp)
                )
            }
        }
        Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Text(
                text = state.schoolName.ifBlank { "—" },
                style = MaterialTheme.typography.headlineSmall,
                fontSize = 24.sp,
                fontWeight = FontWeight.ExtraBold,
                color = SchoolDetailsNavy
            )
            val yearLabel = state.analytics?.currentAcademicYear?.label?.trim().orEmpty()
            Text(
                text = when {
                    state.isAnalyticsLoading -> "…"
                    yearLabel.isNotBlank() -> yearLabel
                    else -> "No current academic year"
                },
                style = MaterialTheme.typography.bodyMedium,
                fontSize = 14.sp,
                fontWeight = FontWeight.Normal,
                color = SchoolDetailsMuted
            )
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun SchoolDetailsSummaryStats(
    stats: List<SchoolDetailStatUi>,
    isLoading: Boolean,
    besideIdentity: Boolean
) {
    Column(
        horizontalAlignment = Alignment.End,
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        if (isLoading) {
            SchoolDetailsStatsShimmer(besideIdentity = besideIdentity)
        } else {
            FlowRow(
                horizontalArrangement = Arrangement.spacedBy(10.dp, Alignment.End),
                verticalArrangement = Arrangement.spacedBy(10.dp),
                maxItemsInEachRow = if (besideIdentity) 5 else 3
            ) {
                stats.forEach { stat ->
                    SchoolDetailsStatCard(
                        stat = stat,
                        modifier = Modifier.width(148.dp).height(64.dp)
                    )
                }
            }
        }
    }
}

@Composable
internal fun SchoolStatusPill(
    active: Boolean,
    label: String
) {
    val contentColor = if (active) SchoolDetailsSuccess else Color(0xFFDC2626)
    Row(
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .size(8.dp)
                .background(contentColor, CircleShape)
        )
        Text(
            text = label,
            fontSize = 14.sp,
            fontWeight = FontWeight.Normal,
            color = contentColor
        )
    }
}

@Composable
internal fun SchoolDetailsSectionCard(
    title: String,
    iconPainter: Painter,
    content: @Composable () -> Unit
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(10.dp),
        colors = CardDefaults.cardColors(containerColor = SchoolDetailsCard),
        border = BorderStroke(1.dp, SchoolDetailsBorder)
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 16.dp, vertical = 14.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .size(30.dp)
                        .background(SchoolDetailsIconTileBg, RoundedCornerShape(8.dp)),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        painter = iconPainter,
                        contentDescription = title,
                        tint = SchoolDetailsPrimary,
                        modifier = Modifier.size(18.dp)
                    )
                }
                Text(
                    text = title,
                    fontSize = 16.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = SchoolDetailsNavy
                )
            }
            content()
        }
    }
}

@Composable
internal fun SchoolDetailsInfoSubsection(
    title: String,
    rows: List<SchoolDetailRowUi>,
    modifier: Modifier = Modifier
) {
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(16.dp)) {
        Column {
            Text(
                text = title,
                fontSize = 16.sp,
                fontWeight = FontWeight.SemiBold,
                color = SchoolDetailsNavy
            )
            Spacer(modifier = Modifier.height(8.dp))
            Spacer(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(1.dp)
                    .background(SchoolDetailsLine)
            )
        }
        SchoolDetailsColumn(rows = rows)
    }
}

@Composable
internal fun SchoolDetailsColumn(
    rows: List<SchoolDetailRowUi>
) {
    Column {
        rows.forEachIndexed { index, row ->
            SchoolDetailsRow(
                label = row.label,
                value = row.value,
                valueColor = row.valueColor,
                showDivider = index != rows.lastIndex
            )
        }
    }
}

@Composable
internal fun SchoolDetailsRow(
    label: String,
    value: String,
    showDivider: Boolean,
    valueColor: Color? = null
) {
    Column {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(min = 31.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = label,
                modifier = Modifier.weight(1f),
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold,
                color = SchoolDetailsNavy
            )
            Text(
                text = value.ifBlank { "—" },
                modifier = Modifier.weight(1.1f),
                fontSize = 14.sp,
                fontWeight = FontWeight.Normal,
                color = valueColor ?: SchoolDetailsNavy
            )
        }
        if (showDivider) {
            Spacer(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(1.dp)
                    .background(SchoolDetailsLine)
            )
        }
    }
}

@Composable
internal fun SchoolDetailsStatCard(
    stat: SchoolDetailStatUi,
    modifier: Modifier = Modifier
) {
    AdminListStatCardShell(
        modifier = modifier,
        surfaceColor = stat.accentBg,
        borderColor = Color.Transparent,
        minHeight = 0.dp,
        contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 10.dp, vertical = 8.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(0.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Box(
                modifier = Modifier
                    .size(24.dp)
                    .background(Color.White.copy(alpha = 0.45f), RoundedCornerShape(6.dp)),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = stat.icon,
                    contentDescription = stat.label,
                    tint = stat.accent,
                    modifier = Modifier.size(18.dp)
                )
            }
            Column(
                modifier = Modifier.weight(1f, fill = false),
                verticalArrangement = Arrangement.spacedBy(2.dp)
            ) {
                Text(
                    text = stat.value,
                    fontSize = 16.sp,
                    fontWeight = FontWeight.ExtraBold,
                    color = SchoolDetailsNavy,
                    maxLines = 1,
                    softWrap = false
                )
                Text(
                    text = stat.label,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = SchoolDetailsMuted,
                    maxLines = 1,
                    softWrap = false,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
    }
}

@Composable
internal fun SchoolDetailsAuditSection(
    audit: SchoolDetailAuditUi,
    compact: Boolean,
    isCreatedByLoading: Boolean = false,
    isUpdatedByLoading: Boolean = false
) {
    if (compact) {
        Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
            SchoolDetailsAuditItem(
                iconPainter = painterResource(Res.drawable.calendar),
                label = "Created On",
                value = audit.createdOn
            )
            SchoolDetailsAuditItem(
                iconPainter = painterResource(Res.drawable.user),
                label = "Created By",
                value = audit.createdBy,
                isLoading = isCreatedByLoading
            )
            SchoolDetailsAuditItem(
                iconPainter = painterResource(Res.drawable.clock),
                label = "Last Updated",
                value = audit.lastUpdated
            )
            SchoolDetailsAuditItem(
                iconPainter = painterResource(Res.drawable.user),
                label = "Updated By",
                value = audit.updatedBy,
                isLoading = isUpdatedByLoading
            )
        }
    } else {
        Row(
            modifier = Modifier.height(IntrinsicSize.Min)
        ) {
            SchoolDetailsAuditItem(
                modifier = Modifier.weight(1f),
                iconPainter = painterResource(Res.drawable.calendar),
                label = "Created On",
                value = audit.createdOn
            )
            SchoolDetailsAuditDivider()
            SchoolDetailsAuditItem(
                modifier = Modifier.weight(1f),
                iconPainter = painterResource(Res.drawable.user),
                label = "Created By",
                value = audit.createdBy,
                isLoading = isCreatedByLoading
            )
            SchoolDetailsAuditDivider()
            SchoolDetailsAuditItem(
                modifier = Modifier.weight(1f),
                iconPainter = painterResource(Res.drawable.clock),
                label = "Last Updated",
                value = audit.lastUpdated
            )
            SchoolDetailsAuditDivider()
            SchoolDetailsAuditItem(
                modifier = Modifier.weight(1f),
                iconPainter = painterResource(Res.drawable.user),
                label = "Updated By",
                value = audit.updatedBy,
                isLoading = isUpdatedByLoading
            )
        }
    }
}

@Composable
private fun SchoolDetailsAuditDivider() {
    Box(
        modifier = Modifier
            .fillMaxHeight()
            .width(1.dp)
            .background(SchoolDetailsLine)
    )
}

@Composable
internal fun SchoolDetailsAuditItem(
    label: String,
    value: String,
    iconPainter: Painter,
    modifier: Modifier = Modifier,
    isLoading: Boolean = false
) {
    Column(
        modifier = modifier.padding(horizontal = 22.dp, vertical = 16.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
            Box(
                modifier = Modifier
                    .size(20.dp)
                    .background(Color.Transparent, CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    painter = iconPainter,
                    contentDescription = label,
                    tint = SchoolDetailsNavy,
                    modifier = Modifier.size(18.dp)
                )
            }
            Text(
                text = label,
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold,
                color = SchoolDetailsNavy
            )
        }
        if (isLoading) {
            val transition = rememberInfiniteTransition(label = "school_details_audit_shimmer")
            val shimmerX by transition.animateFloat(
                initialValue = -120f,
                targetValue = 240f,
                animationSpec = infiniteRepeatable(tween(900), RepeatMode.Restart),
                label = "school_details_audit_shimmer_x"
            )
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(18.dp)
                    .clip(RoundedCornerShape(5.dp))
                    .background(
                        Brush.linearGradient(
                            colors = listOf(Color(0xFFE8EDF6), Color(0xFFF8FAFD), Color(0xFFE8EDF6)),
                            start = Offset(shimmerX, 0f),
                            end = Offset(shimmerX + 120f, 0f)
                        )
                    )
            )
        } else {
            Text(
                text = value.ifBlank { "—" },
                fontSize = 14.sp,
                fontWeight = FontWeight.Normal,
                color = SchoolDetailsNavy
            )
        }
    }
}

@Composable
internal fun SchoolDetailsCloseButton(
    onClose: () -> Unit
) {
    Surface(
        modifier = Modifier.size(width = 166.dp, height = 44.dp),
        shape = RoundedCornerShape(8.dp),
        color = SchoolDetailsCard,
        border = BorderStroke(1.dp, SchoolDetailsBorder),
        onClick = onClose
    ) {
        Box(contentAlignment = Alignment.Center) {
            Text(
                text = "Close",
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.SemiBold,
                color = SchoolDetailsNavy
            )
        }
    }
}
